/**
 * SidebarContent.jsx — 사이드바 내부 콘텐츠 컴포넌트 (admin/employee 공용, 데스크탑/모바일 공용)
 *
 * 브랜드 영역, 작업 대상 선택창(CompanyScopeSelector), 선택 회사의 메뉴 그룹,
 * 다크모드 토글, 로그아웃 버튼을 렌더링합니다.
 *
 * 변경 이력:
 *   - 2026-04-27 (Step 2): admin 전용 → admin/employee 공용 인터페이스로 일반화
 *   - 2026-08-24 (카테고리 재편): 관리자용 "내 정보" 구역 해체 — 직원 열람(드롭다운) 항목을
 *     같은 주제 카테고리 그룹 안에 합류. 직원 로그인 화면의 "내 정보"는 불변.
 *   - 2026-09-03 (개편 1차): 회사별 아코디언 폐지 → 상단 선택창 + 선택 회사 메뉴 한 벌
 *     (매트릭스는 companyMenuSet.js 로 이식, 링크는 기존 ext 라우트 재사용 — 서버 무변경).
 *   - 2026-09-15: 직원 열람("…열람" + 직원 선택) 줄 전부 제거 (사용자 요청). 관리자는 관리 메뉴만.
 *   - 2026-10-01: 작업 대상 "통합관리" — 통합 모드면 메뉴 자리에 통합 메뉴만(IntegratedSidebarNav), 홈·상황판 바로가기는
 *     숨기고 "내 정보"는 그대로(사용자 확정). 일반 사이드바의 '통합 관리' 묶음은 뺐다(들어가는 길 = 회사 칸 맨 아래).
 *   - 2026-10-06: "내 정보는 고정이야 어떤 작업 대상을 고를지라도"(사용자 확정) — 통합 모드에서도 홈·상황판 줄을 숨기지 않는다.
 *     숨기면 '내 정보'가 파란 "통합관리" 칸 바로 밑으로 올라붙어 통합관리 메뉴처럼 보였다(파인그룹 직원 신고).
 *     작업 대상에 따라 바뀌는 것은 구분선 아래 메뉴 자리뿐이다.
 */
import React from 'react';
import { LayoutDashboard, Layers, Home } from 'lucide-react';
import { cn } from '../../../lib/utils';
import MenuItem from './MenuItem';
import SidebarHeader from './SidebarHeader';
import SidebarFooter from './SidebarFooter';
import CompanyScopeSelector from './CompanyScopeSelector';
import { useCompanyScope } from './CompanyScopeContext';
import { ADMIN_ONLY_MENUS } from './menuConfig';
// 메뉴 조립·분류는 홈 카드와 한 곳에서 (2026-09-15) — 분류는 DB(saas_modules.category)
import { composeAdminMenus } from './menuComposition';
import { useMenuCategories } from './menuCategories';
import { resolveMenuLabel } from './recentContractMenuLabel';
import { useRecentContractsMenuMonths } from './useRecentContractsMenuMonths';
// 옵션 3 (2026-04-27): 권한 기반 메뉴 필터링 헬퍼 분리 — sidebarFilters.js 로 추출 (300줄 룰)
import { isAdminUser, filterAdminOnlyMenus } from './sidebarFilters';
import { useShortcutEdit } from './useShortcutEdit';
import { SidebarEditContext } from './SidebarEditContext';
import { sidebarIntegratedMenus } from '../attendancehub/integratedAttendanceTabs';
import IntegratedSidebarNav from './IntegratedSidebarNav';
import FavoritesSection from './FavoritesSection';
import { useFavorites } from './useFavorites';

// Props: location/isMobile/userRole(admin|employee)/permissions(MY_MENUS 필터)/integratedMenus/setMobileOpen/isDark/toggleDark/onLogout
// 2026-09-03 개편 1차: 회사 아코디언 → 상단 선택창(CompanyScopeSelector) + 선택 회사의 메뉴 한 벌.
export function SidebarContent({
  location,
  isMobile,
  userRole = 'admin',  // 'admin' | 'employee' — 메타 정보 (옵션 3 이후 메뉴 가드는 permCodes 기반)
  permissions,         // MY_MENUS 권한 필터링용 (canUse* boolean) — 없으면 MY_MENUS 그룹 미표시
  permCodes,           // 옵션 3 (2026-04-27) — MENU_GROUPS / ADMIN_ONLY_MENUS 권한 필터용 Set
  me,                  // Step 8 — 사이드바 푸터 사용자 정보 표시용
  onPasswordChange,    // Step 8 — 비밀번호 변경 모달 수동 열기 콜백
  integratedMenus = [],
  setMobileOpen,
  isDark,
  toggleDark,
  onLogout,
  // 모바일 헤더 바로가기 (2026-04-27 신규) — AdminLayout 에서 localStorage 와 동기화
  shortcuts = [],         // 현재 저장된 path 배열 (최대 5개)
  onSaveShortcuts,        // 편집 완료 시 호출 (paths 인자) — AdminLayout 이 localStorage 저장 + 헤더 재렌더
}) {
  const isEmployeeLayout = userRole === 'employee';
  const isAdmin = isAdminUser();
  const scope = useCompanyScope();
  const cats = useMenuCategories();
  // 메뉴 조립(권한 필터·회사 선택·고객 테넌트 보기)과 분류는 menuComposition 한 곳에서.
  const { menuGroups, myMenus, hideMyMenus, visibleGroups } = composeAdminMenus({ cats, permissions, permCodes, isAdmin, scope });
  const integratedMode = Boolean(scope?.integrated);
  const recentContractMonths = useRecentContractsMenuMonths(isAdmin ? [] : myMenus);
  const displayMyMenus = myMenus.map((menu) => ({
    ...menu,
    label: resolveMenuLabel(menu, recentContractMonths),
  }));
  const visibleAdminOnly = filterAdminOnlyMenus(ADMIN_ONLY_MENUS, isAdmin);
  const canSeeSituationBoard = isAdmin || Boolean(permCodes && permCodes.has('SITUATION_BOARD'));
  // 통합 메뉴 비상구 (2026-10-01): 통합 메뉴는 회사 칸의 "통합관리"로 들어간다. 운영자가 아닌데 열쇠가 있고
  //   그 칸이 안 그려지는 경우에만(지금 규칙으론 생기지 않음 — 칸 규칙이 바뀌어도 길을 잃지 않게) 예전 묶음을 맨 끝에 그린다.
  //   운영자는 뺀다 — 서버가 전 테넌트 메뉴를 주므로 고객 테넌트 화면에 운영사 메뉴가 섞인다.
  //   짝이 있는 출퇴근 달력 메뉴는 뺀다(그 출퇴근 메뉴 안 [달력] 탭, 2026-09-30).
  const fallbackIntegratedMenus = (!isAdmin && !scope?.canUseIntegrated) ? sidebarIntegratedMenus(integratedMenus) : [];
  // 편집 모드 hook — 모바일 헤더 바로가기 등록용. MenuItem 은 SidebarEditContext 에서 자동 주입
  const { editMode, selections, startEdit, toggle, save, cancel } = useShortcutEdit(shortcuts, onSaveShortcuts);
  // PC 즐겨찾기(2026-10-22) — 휴대폰은 쉰다(위쪽 바로가기가 따로 있다). 별은 MenuItem 이 이 값을 읽어 단다.
  const fav = useFavorites({
    active: !isMobile && !editMode, cats, permissions, permCodes, isAdmin, scope,
    myMenus: hideMyMenus ? [] : displayMyMenus, fallbackIntegrated: fallbackIntegratedMenus,
  });
  return (
    <SidebarEditContext.Provider value={{ editMode, selections, toggle, fav }}>
      {/* ── 브랜드 영역 (분리: SidebarHeader.jsx — 카드 형태 + 회사 PWA 로고 + admin/employee 분기) ── */}
      <SidebarHeader isAdmin={isAdmin} me={me} />

      {/* ── 작업 대상 선택창 (2026-09-03 개편 1차) — 권한 따라 자동 축소, 일반 직원은 비노출 ── */}
      <CompanyScopeSelector />

      {/* ── 홈 바로가기 (2026-08-26 A안): 운영자=항상, 그 외=HOME 권한 보유 시만(테넌트 관리자 부여분).
          레이아웃 종류로는 안 막는다 — 테넌트 관리자가 개인 상황판(/my/board) 등 직원 레이아웃 화면에
          있어도 홈이 보여야 하므로. 직원은 HOME 권한이 없어(위임 불가) 어차피 비노출. ── */}
      {(isAdmin || Boolean(permCodes && permCodes.has('HOME'))) && (
        <div className="px-2.5 pb-1">
          <MenuItem
            to="/admin/home"
            label="홈"
            Icon={Home}
            isActive={location.pathname === '/admin/home'}
            onClick={() => setMobileOpen(false)}
            favoritable={false}
          />
        </div>
      )}

      {/* ── 상황판 바로가기: 위치는 고정, 노출은 SITUATION_BOARD 배정 기준(작업 대상과 무관 — 2026-10-06) ── */}
      {canSeeSituationBoard && (
        <div className="px-2.5 pb-1">
          <MenuItem
            to={isEmployeeLayout ? '/my/board' : '/admin/board'}
            label="상황판"
            Icon={LayoutDashboard}
            isActive={location.pathname === (isEmployeeLayout ? '/my/board' : '/admin/board')}
            onClick={() => setMobileOpen(false)}
            favoritable={false}
          />
        </div>
      )}

      {/* ── 내 정보 그룹 ── 직원(employee) 전용: 본인 데이터 메뉴. 관리자·테넌트 관리자는 이 구역이 없다.
          어떤 작업 대상(회사·테넌트·통합관리)을 골라도 같은 자리·같은 모양으로 고정(2026-10-06 사용자 확정). */}
      {!hideMyMenus && displayMyMenus.length > 0 && (
        <div className={cn(
          'px-2.5 pb-1 flex flex-col gap-[1px]',
          // 모바일은 전체가 한 덩어리로 스크롤하므로 여기만 따로 잘리지 않게 한다.
          isMobile ? '' : 'max-h-[260px] overflow-y-auto overscroll-contain',
        )}>
          <div className="text-[9px] font-black text-[var(--text-muted)] uppercase tracking-[0.08em] px-2 pt-1 pb-1">
            내 정보
          </div>
          {displayMyMenus.map((m) => (
            <MenuItem
              key={m.to}
              to={m.to}
              label={m.label}
              Icon={m.Icon}
              isActive={location.pathname === m.to}
              onClick={() => setMobileOpen(false)}
            />
          ))}
        </div>
      )}

      {/* ── 즐겨찾기 (PC, 2026-10-22) — '내 정보' 바로 아래, 내 정보가 없으면 상황판 바로 아래. 작업 대상과 무관하게 고정,
          하나도 없으면 칸 없음(사용자 확정). 회사 표시는 작업 대상을 고를 수 있는 사람만. ── */}
      <FavoritesSection
        fav={fav}
        pathname={location.pathname}
        hqCode={scope?.hqCompanyCode}
        showMarks={Boolean(scope?.showSelector)}
      />

      {/* 구분선 */}
      <div className="h-px bg-[var(--border)] my-1.5" />

      {/* ── 메뉴 그룹 목록 (옵션 3: visibleGroups — permCode 보유분만, 빈 그룹 자동 제거) ──
          모바일은 **사이드바 전체가 한 덩어리로** 스크롤한다 (2026-09-21 사용자 요청).
            전에는 이 nav 만 따로 스크롤해서, 위(회사칸·작업대상·홈/상황판 234px)와
            아래(푸터 141px)가 자리를 차지해 메뉴 보이는 창이 279px 뿐이었다.
            메뉴 전체 길이가 1,200px 넘어 아이폰에서 네다섯 번 넘겨야 아래 분류에 닿았다.
            자체 스크롤을 없애면 같은 화면에서 693px 을 쓴다(2.5배). 데스크톱은 화면이 넓고
            로그아웃이 늘 보이는 편이 나아 종전대로 둔다. */}
      <nav className={cn(
        'flex flex-col px-2.5 gap-0 pb-2',
        isMobile ? '' : 'flex-1 overflow-y-auto',
      )}>
        {integratedMode && (
          <IntegratedSidebarNav
            menus={scope.integratedMenus}
            menuGroups={menuGroups}
            dbOrder={cats?.order}
            hqCode={scope.hqCompanyCode}
            pathname={location.pathname}
            canManage={isAdmin || Boolean(scope?.canManageIntegrated)}
            isMobile={isMobile}
            onNavigate={() => setMobileOpen(false)}
          />
        )}
        {!integratedMode && visibleGroups
          // 상황판은 관리자 사이드바 최상단 고정 바로가기로 따로 표시한다.
          .filter((g) => g.group !== '현황')
          .map((group) => (
            <div key={group.group} className="flex flex-col gap-[1px] pb-1">
              {/* 그룹 소제목 라벨 */}
              <div className="text-[9px] font-black text-[var(--text-muted)] uppercase tracking-[0.08em] px-2 pt-2 pb-1">
                {group.group}
              </div>

              {/* 카테고리 내 항목 = 고정 메뉴 + admin 전용('시스템')을 하나로 합쳐
                  ㄱ~ㅎ(가나다) 정렬 (2026-08-24 사용자 명세 — 같은 주제가 흩어져 보이지 않게) */}
              {[
                ...group.items,
                ...(group.group === '시스템' ? visibleAdminOnly : []),
              ]
                .sort((a, b) => String(a.label).localeCompare(String(b.label), 'ko'))
                .map((m) => (
                  <MenuItem
                    key={m.to}
                    to={m.to}
                    label={m.label}
                    Icon={m.Icon}
                    isActive={location.pathname === m.to}
                    onClick={() => setMobileOpen(false)}
                  />
                ))}
            </div>
          ))}
        {!integratedMode && fallbackIntegratedMenus.length > 0 && (
          <div className="flex flex-col gap-[1px] pb-1">
            <div className="text-[9px] font-black text-[var(--text-muted)] uppercase tracking-[0.08em] px-2 pt-2 pb-1">
              통합 관리
            </div>
            {fallbackIntegratedMenus.map((m) => (
              <MenuItem
                key={`im-${m.id}`}
                to={`/admin/integrated/${m.id}`}
                label={m.name}
                Icon={Layers}
                isActive={location.pathname === `/admin/integrated/${m.id}`}
                onClick={() => setMobileOpen(false)}
              />
            ))}
          </div>
        )}
      </nav>

      {/* 푸터 분리: SidebarFooter — 평소(바로가기 등록·비밀번호·다크·로그아웃) / 편집(완료·취소) */}
      <SidebarFooter
        editMode={editMode} selections={selections} onSave={save} onCancel={cancel}
        isMobile={isMobile} onStartEdit={startEdit}
        onPasswordChange={onPasswordChange}
        isDark={isDark} toggleDark={toggleDark}
        onLogout={onLogout}
      />
    </SidebarEditContext.Provider>
  );
}
